import { renderToStaticMarkup } from "react-dom/server"

import { extractArtifacts } from "@/features/artifacts/streaming/artifact-stream-parser"
import {
  FENCE_LINE_PATTERN,
  findOpenFence,
  maskFencedCodeForDelimiterDetection,
  splitFenceLines,
} from "@/lib/fence-tracking"
import { logger } from "@/lib/logger"
import {
  createCitationPattern,
  createQuotedCitationValuePattern,
  formatCitationDisplay,
} from "@/utils/citation-utils"

import { getCitationType } from "./citation.utils"

const citationHtmlCache = new Map<string, string>()

function getOrCreateCitationHtml(cacheKey: string, createHtml: () => string): string {
  const cachedHtml = citationHtmlCache.get(cacheKey)
  if (cachedHtml) {
    return cachedHtml
  }

  if (citationHtmlCache.size > 500) {
    citationHtmlCache.clear()
  }

  const citationHtml = createHtml()
  citationHtmlCache.set(cacheKey, citationHtml)

  return citationHtml
}

export type HtmlParsedAnswer = {
  answerHtml: string
  citations: string[]
}

/**
 * True if the text currently ends inside an open (unclosed) code fence.
 * Used to avoid mistaking `|`-prefixed lines inside a still-streaming code
 * block for an open table.
 */
function endsInsideOpenCodeFence(lines: string[]): boolean {
  return findOpenFence(lines) !== null
}

/**
 * During streaming, wraps a still-growing GFM table — a trailing run of 2+
 * `|`-prefixed lines not yet closed by a blank line — in a fenced code
 * block. remark-gfm's table tokenizer has superlinear cost on a large
 * table (benchmarked up to ~175ms at 50KB); wrapping it turns that into
 * cheap fenced-code parsing (~7ms even at 100KB) instead. The inserted
 * fence is picked up as just another open fence by
 * downgradeUnclosedFenceWhileStreaming (called right after this), so
 * CodeBlock renders it via the same plain-text pending path as an unclosed
 * code fence until the table actually closes, then it reverts to a real,
 * fully-parsed table — findSafeSplitPoints already treats an open fence as
 * unsafe to freeze inside, so no changes are needed there either.
 */
function wrapOpenTableWhileStreaming(markdown: string): string {
  if (markdown.length === 0 || /\n[ \t]*\n[ \t]*$/.test(markdown)) {
    // Nothing to wrap, or already closed by a genuine blank line.
    return markdown
  }

  if (endsInsideOpenCodeFence(splitFenceLines(markdown).map(({ content }) => content))) {
    return markdown
  }
  const lines = markdown.split("\n")

  let openTableStart: number | null = null
  for (let lineIndex = lines.length - 1; lineIndex >= 0; lineIndex--) {
    const line = lines[lineIndex]
    if (line.trim() === "") {
      if (lineIndex === lines.length - 1) continue // trailing "\n", not a closing blank line
      break
    }
    if (!line.trimStart().startsWith("|")) break
    openTableStart = lineIndex
  }

  if (openTableStart === null) return markdown

  const lastContentIndex = lines[lines.length - 1] === "" ? lines.length - 2 : lines.length - 1
  if (lastContentIndex - openTableStart + 1 < 2) return markdown // need header + separator

  lines.splice(openTableStart, 0, "```table")
  return lines.join("\n")
}

/**
 * During streaming, rewrites an unclosed fenced code block's language to
 * `<lang>_pending` (or `text_pending` if no language was given) so
 * CodeBlock can skip expensive rendering — Prism syntax highlighting for any
 * language, the live Mermaid diagram for ```mermaid — until the fence
 * actually closes, instead of redoing that work on every streamed chunk.
 * Once the fence is closed, it reverts to its real language and renders
 * normally.
 */
function downgradeUnclosedFenceWhileStreaming(markdown: string): string {
  const lines = splitFenceLines(markdown)
  const open = findOpenFence(lines.map(({ content }) => content))

  if (open) {
    const originalLine = lines[open.lineIndex].content
    const fenceMatch = FENCE_LINE_PATTERN.exec(originalLine)
    if (fenceMatch) {
      const [fullMatch, indent, marker, spacing, lang] = fenceMatch
      // CodeBlock detects the pending state with /language-(\w+)/, so the
      // rewritten tag must itself be \w-safe. `lang` can be any non-whitespace
      // info string (e.g. "c++", "objective-c") — take only its leading \w run
      // so a symbolic language doesn't swallow the "_pending" suffix into an
      // unmatched trailing chunk and silently lose the pending fast path.
      const safeLang = /^\w+/.exec(lang)?.[0]
      lines[open.lineIndex].content =
        `${indent}${marker}${spacing}${safeLang || "text"}_pending` +
        originalLine.slice(fullMatch.length)
    }
    return lines.map(({ content, separator }) => content + separator).join("")
  }

  return markdown
}

/**
 * Converts LaTeX-style math delimiters that remark-math v6 does not
 * recognise into the dollar-sign equivalents that it does:
 *   \[...\]  →  $$...$$   (display math)
 *   \(...\)  →  $...$     (inline math)
 *
 * Skips occurrences inside fenced code blocks.
 */
function convertLatexDelimiters(markdown: string): string {
  const lines = markdown.split("\n")
  let inCodeFence = false
  let codeFenceMarker: string | null = null

  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trimStart()
    const fenceMatch = trimmed.match(/^(```|~~~)/)

    if (fenceMatch) {
      if (!inCodeFence) {
        inCodeFence = true
        codeFenceMarker = fenceMatch[1]
        continue
      } else if (trimmed.startsWith(codeFenceMarker!)) {
        inCodeFence = false
        codeFenceMarker = null
        continue
      }
    }

    if (!inCodeFence) {
      // Display math: \[...\] → $$...$$
      // Note: "$$$$" produces "$$" because $$ is a special replacement pattern in JS
      lines[i] = lines[i].replace(/\\\[/g, "$$$$")
      lines[i] = lines[i].replace(/\\\]/g, "$$$$")
      // Inline math: \(...\) → $...$
      lines[i] = lines[i].replace(/\\\(/g, "$")
      lines[i] = lines[i].replace(/\\\)/g, "$")
    }
  }

  return lines.join("\n")
}

/**
 * During streaming, trims content from the last unmatched math delimiter
 * onwards so partial LaTeX doesn't flash as raw text before the closing
 * delimiter arrives.  Handles $$, \[ and \(.
 */
function hideUnmatchedMathDelimiters(markdown: string): string {
  // Note: \[...\] and \(...\) are already converted to $$/$  by convertLatexDelimiters
  // before this function runs, so we only need to handle dollar-sign delimiters.
  const detectionMarkdown = maskFencedCodeForDelimiterDetection(markdown)

  // Check for unclosed $$ (display math)
  // Count occurrences — odd count means one is unclosed
  const ddMatches = detectionMarkdown.match(/\$\$/g)
  if (ddMatches && ddMatches.length % 2 === 1) {
    const lastDD = detectionMarkdown.lastIndexOf("$$")
    return markdown.substring(0, lastDD)
  }

  // Check for unclosed $ (inline math) on the last line
  // Only check the last line to avoid false positives with $ in earlier text
  const lastNewline = Math.max(
    detectionMarkdown.lastIndexOf("\n"),
    detectionMarkdown.lastIndexOf("\r")
  )
  const lastLine = detectionMarkdown.substring(lastNewline + 1)
  const dollarMatches = lastLine.match(/(?<!\\)\$/g)
  if (dollarMatches && dollarMatches.length % 2 === 1) {
    const lastDollar = detectionMarkdown.lastIndexOf("$")
    // Don't trim if it's part of $$
    if (
      lastDollar > 0 &&
      detectionMarkdown[lastDollar - 1] !== "$" &&
      (lastDollar + 1 >= detectionMarkdown.length || detectionMarkdown[lastDollar + 1] !== "$")
    ) {
      return markdown.substring(0, lastDollar)
    }
  }

  return markdown
}

type ParseAnswerParams = {
  answer: string
  isStreaming: boolean
  noCitation: boolean
  citationSection: string
  onCitationClicked?: (citationFilePath: string) => void
  onCitationNumberClicked?: (citationIndex: number) => void
  messageIndex?: number
  // Same `${sessionId}:${index}` scope the artifact store uses as its ingest key,
  // so display links for artifacts that omit `identifier` (fallback `artifact-…`)
  // resolve to the same store key. Without it the chat link and store key diverge
  // and clicking an unnamed artifact wouldn't open the panel.
  messageScope?: string
}

/**
 * Normalize multi-citations ([[cite1][cite2]] → [[cite1]][[cite2]]) only outside
 * fenced and inline code blocks. Preserves code block content verbatim.
 */
function normalizeMultiCitationsOutsideCode(text: string): string {
  // Group 1 is kept verbatim: fenced code, inline code, and a quoted citation
  // value (`quote="arr[0][1]"`), so a `][` inside a quote is not mistaken for
  // two combined citations. Group 2 is the `][` to split.
  const keptSpan = [
    /```[\s\S]*?(?:```|$)/.source,
    /`[^`\n]*`/.source,
    createQuotedCitationValuePattern().source,
  ].join("|")
  const pattern = new RegExp(`(${keptSpan})|(\\]\\[(?![[\\]]))`, "g")
  return text.replace(pattern, (match, keptText: string | undefined) => (keptText ? match : "]][["))
}

/**
 * Helper to check if a position in the text is inside a code block
 */
function isInsideCodeBlock(text: string, position: number): boolean {
  // Check for fenced code blocks (```)
  const beforePosition = text.substring(0, position)

  // Count triple backticks before position
  const tripleBackticksBeforeCount = (beforePosition.match(/```/g) || []).length

  // If odd number of triple backticks before position, we're inside a fenced block.
  // We don't require a closing ``` after because during streaming it may not have arrived yet.
  if (tripleBackticksBeforeCount % 2 === 1) {
    return true
  }

  // Check for inline code blocks (inline backticks, including multi-backtick delimiters)
  // Inline code in Markdown does not cross line boundaries, so we only need to look at the current line.
  const lineStart = (() => {
    const prevNewline = text.lastIndexOf("\n", position - 1)
    return prevNewline === -1 ? 0 : prevNewline + 1
  })()
  const lineEnd = (() => {
    const nextNewline = text.indexOf("\n", position)
    return nextNewline === -1 ? text.length : nextNewline
  })()

  // If the position is outside the bounds of this line segment, it cannot be inside inline code.
  if (position < lineStart || position > lineEnd) {
    return false
  }

  let isInsideInlineCode = false
  let currentDelimiterLength = 0

  let i = lineStart
  while (i < lineEnd) {
    // If we've reached the target position, report whether we're currently inside inline code.
    if (i === position) {
      return isInsideInlineCode
    }

    if (text[i] === "`") {
      // Count the length of this run of consecutive backticks.
      let j = i
      while (j < lineEnd && text[j] === "`") {
        j++
      }
      const runLength = j - i

      if (!isInsideInlineCode) {
        // Opening delimiter for an inline code span.
        isInsideInlineCode = true
        currentDelimiterLength = runLength
      } else {
        if (runLength === currentDelimiterLength) {
          // Matching closing delimiter for the current inline code span.
          isInsideInlineCode = false
          currentDelimiterLength = 0
        }
        // If runLength != currentDelimiterLength, these backticks are treated as literal characters
        // inside the existing inline code span, so we leave the state unchanged.
      }

      // Skip past this run of backticks.
      i = j
      continue
    }

    i++
  }

  return isInsideInlineCode
}

function isExtractionCitation(citation: string): boolean {
  return getCitationType(citation) === "extraction"
}

function getExtractionCitationLabel(citation: string): string {
  if (citation.toLowerCase().startsWith("req:")) {
    return citation.slice(4).trim()
  }

  return citation.trim()
}

function flushConsecutiveCitations(
  fragments: string[],
  citationIndexes: number[],
  inputFormat: "html" | "md"
): number[] {
  if (citationIndexes.length === 0) {
    return citationIndexes
  }

  if (inputFormat === "html") {
    const citationText = citationIndexes.map((idx) => `[${idx}]`).join(", ")
    fragments.push(citationText)
  } else {
    fragments.push(" " + citationIndexes.map((idx) => `[${idx}]`).join(" "))
  }

  return []
}

export function parseAnswerToHtml({
  answer,
  isStreaming,
  noCitation,
  messageIndex,
  messageScope,
}: ParseAnswerParams): HtmlParsedAnswer {
  const citations: string[] = []

  // trim any whitespace from the end of the answer after removing follow-up questions
  let parsedAnswer = answer.trimEnd()

  // Strip artifact blocks before any downstream processing (citations, math, mermaid, etc.)
  // Scope must match the store's ingest key so unnamed-artifact links resolve.
  parsedAnswer = extractArtifacts(parsedAnswer, messageScope ?? "").displayText

  // Convert \[...\] and \(...\) to $$...$$ and $...$ so remark-math can parse them
  parsedAnswer = convertLatexDelimiters(parsedAnswer)

  // Hide incomplete citations during streaming to prevent visual flash
  if (isStreaming) {
    const lastOpenBracket = parsedAnswer.lastIndexOf("[[")
    if (lastOpenBracket !== -1) {
      const afterLastOpen = parsedAnswer.substring(lastOpenBracket)
      if (!afterLastOpen.includes("]]")) {
        const pendingLength = parsedAnswer.length - lastOpenBracket
        if (pendingLength < 300) {
          parsedAnswer = parsedAnswer.substring(0, lastOpenBracket)
        }
      }
    }
    // Also hide trailing [ that might become [[
    if (parsedAnswer.endsWith("[")) {
      parsedAnswer = parsedAnswer.slice(0, -1)
    }

    // Detect incomplete math on the original Markdown before adding a
    // synthetic table fence or rewriting a pending fence's info string.
    parsedAnswer = hideUnmatchedMathDelimiters(parsedAnswer)

    // Prevent a still-growing table from re-running remark-gfm's expensive
    // table tokenizer on every chunk (see wrapOpenTableWhileStreaming)
    parsedAnswer = wrapOpenTableWhileStreaming(parsedAnswer)

    // Prevent unclosed code fences from triggering repeated expensive renders
    parsedAnswer = downgradeUnclosedFenceWhileStreaming(parsedAnswer)
  }

  if (noCitation) {
    return {
      answerHtml: parsedAnswer,
      citations: [],
    }
  }

  // Normalize multi-citations: [[cite1][cite2]] → [[cite1]][[cite2]]
  parsedAnswer = normalizeMultiCitationsOutsideCode(parsedAnswer)

  // Find all citation positions and check if they're in code blocks
  const citationPattern = createCitationPattern()
  const fragments: string[] = []
  let lastIndex = 0

  // Use matchAll for safer iteration
  const matches = Array.from(parsedAnswer.matchAll(citationPattern))

  for (const match of matches) {
    const matchStart = match.index!
    const matchEnd = matchStart + match[0].length
    const citationContent = match[1]

    // Add the text before this citation
    fragments.push(parsedAnswer.substring(lastIndex, matchStart))

    // Check if this citation is inside a code block
    const insideCodeBlock = isInsideCodeBlock(parsedAnswer, matchStart)

    if (insideCodeBlock) {
      // Keep the original [[citation]] text in code blocks
      fragments.push(match[0])
    } else if (isExtractionCitation(citationContent)) {
      const extractionLabel = getExtractionCitationLabel(citationContent)
      const cacheKey = `extraction::${citationContent}::${messageIndex ?? ""}`
      const citationHtml = getOrCreateCitationHtml(cacheKey, () =>
        renderToStaticMarkup(
          <button
            className="supContainer inline-flex align-baseline"
            data-citation-raw={citationContent}
            data-message-index={messageIndex}
            type="button"
          >
            <span className="border-primary bg-primary/15 text-foreground hover:bg-primary/25 inline-flex items-center rounded border px-1.5 py-px text-[10px] font-medium transition-colors">
              {extractionLabel}
            </span>
          </button>
        )
      )
      fragments.push(citationHtml)
    } else {
      // Replace with markdown link that the custom `a` component renders as a citation button.
      // Using a link instead of raw HTML ensures citations work inside GFM table cells.
      let citationIndex: number
      if (citations.indexOf(citationContent) !== -1) {
        citationIndex = citations.indexOf(citationContent) + 1
      } else {
        citations.push(citationContent)
        citationIndex = citations.length
      }

      fragments.push(`[${citationIndex}](cite:${citationIndex - 1})`)
    }

    lastIndex = matchEnd
  }

  // Add any remaining text after the last citation
  fragments.push(parsedAnswer.substring(lastIndex))

  return {
    answerHtml: fragments.join(""),
    citations,
  }
}

/**
 * Transforms text containing [[citation]] syntax into either HTML or Markdown format with numbered citations
 * and corresponding reference list at the bottom.
 *
 * Citations are numbered sequentially as they appear and deduplicated.
 * For URLs, creates clickable links in the reference list.
 *
 * @example HTML:
 * "Some fact based on two sources [[source1]][[source2]]" ->
 * "Some fact based on two sources <sup><a href='#citation-1'>[1]</a></sup> <sup><a href='#citation-2'>[2]</a></sup>"
 *
 * "<ol><li id='citation-1'>source1</li><li id='citation-2'>source2</li></ol>"
 *
 * @example Markdown:
 * "Some fact based on two sources [[source1]][[source2]]."
 * ->
 * "Some fact based on two sources [1] [2]"
 * "[1]: source1"
 * "[2]: source2."
 *
 * @param markdownContent - Text containing [[citation]] syntax to be transformed
 * @param inputFormat - Output format, either "html" or "md" (markdown)
 * @returns Transformed text with numbered citations and reference list
 */
export function replaceCitationsWithLinks(
  markdownContent: string,
  inputFormat: "html" | "md"
): string {
  try {
    // Normalize multi-citations: [[cite1][cite2]] → [[cite1]][[cite2]]
    markdownContent = normalizeMultiCitationsOutsideCode(markdownContent)

    const citationPattern = createCitationPattern()
    const matches = Array.from(markdownContent.matchAll(citationPattern))
    // split e.g. "text [[cite1]] more [[cite2]]" -> ["text ", "cite1", " more ", "cite2", ""]
    const parts = String(markdownContent).split(citationPattern)
    const citations: string[] = []
    const citationMap = new Map<string, number>()
    const fragments: string[] = []
    let consecutiveCitations: number[] = []
    let citationMatchIndex = 0

    parts.forEach((part, i) => {
      // Text between citations
      if (i % 2 === 0) {
        consecutiveCitations = flushConsecutiveCitations(
          fragments,
          consecutiveCitations,
          inputFormat
        )
        // Preserve newlines while trimming spaces on each line
        // This is critical for maintaining markdown list structure
        if (part) {
          // Split by newlines, trim trailing spaces from each line, then rejoin
          // This preserves the newline structure while cleaning up whitespace
          const lines = part.split("\n")
          const processedLines = lines.map((line, lineIndex) => {
            // Trim trailing spaces from each line
            const trimmed = line.trimEnd()
            // For the first line, also trim leading spaces if it's not empty
            // This handles cases where the part starts with spaces
            if (lineIndex === 0 && trimmed) {
              return trimmed.trimStart()
            }
            return trimmed
          })
          const processedPart = processedLines.join("\n")
          if (processedPart) {
            fragments.push(processedPart)
          }
        }
      } else {
        // Citation
        const match = matches[citationMatchIndex++]
        const insideCodeBlock = isInsideCodeBlock(markdownContent, match.index!)

        if (insideCodeBlock) {
          // Keep the original [[citation]] text in code blocks
          fragments.push(`[[${part}]]`)
        } else if (isExtractionCitation(part)) {
          consecutiveCitations = flushConsecutiveCitations(
            fragments,
            consecutiveCitations,
            inputFormat
          )
          fragments.push(`[${getExtractionCitationLabel(part)}]`)
        } else {
          let citationIndex: number
          if (citationMap.has(part)) {
            citationIndex = citationMap.get(part)!
          } else {
            citations.push(part)
            citationIndex = citations.length
            citationMap.set(part, citationIndex)
          }
          consecutiveCitations.push(citationIndex)
        }
      }
    })

    // Handle any remaining consecutive citations
    consecutiveCitations = flushConsecutiveCitations(fragments, consecutiveCitations, inputFormat)

    let content = fragments.join("")

    // Add references for both HTML and Markdown as a standard section
    const sortedCitations = Array.from(citationMap.entries())
      .sort((a, b) => a[1] - b[1])
      .map(([citation]) => citation)

    if (inputFormat === "md") {
      content += "\n\n---\n\n\n"
      sortedCitations.forEach((citation) => {
        const num = citationMap.get(citation)!
        content += `[${num}] ${formatCitationDisplay(citation).fileNameWithPage}\n\n`
      })
    } else {
      content += "\n<hr/>\n\n<ol>"
      sortedCitations.forEach((citation) => {
        content += `<li>[${formatCitationDisplay(citation).fileNameWithPage}]</li>`
      })
      content += "\n</ol>"
    }

    return content
  } catch (error) {
    logger.error("Error replacing citations with links:", error as Error)
    return markdownContent
  }
}
